import type { PillTone } from '../../components';
import { formatCents, formatRate } from '../../lib/format/money';
import type { EffectiveLever, LeverValue } from './leversApi';

/** Human labels for levers; the key is the BE lever key. */
export const LEVER_LABEL: Record<string, { label: string; hint?: string }> = {
  bufferFeeBasis: { label: 'Buffer basis', hint: 'gross or running total' },
  bufferFeePercentage: { label: 'Buffer fee', hint: 'reserved against uncollected local tax' },
  optionalGrossPercentFee: { label: 'Gross % fee' },
  retailDeliveryFeeCents: { label: 'Retail delivery fee' },
  rdfTaxable: { label: 'RDF taxable' },
  roundUpIncrementCents: { label: 'Round-up increment', hint: 'the revenue model' },
  minRevenueCents: { label: 'Minimum revenue' },
  cancellationFeeCents: { label: 'Cancellation fee', hint: '0 = a cancel voids' },
  defaultFoodTaxCode: { label: 'Food tax code' },
  alternateFoodTaxCode: { label: 'Alternate tax code' },
  maxPriceCents: { label: 'Max price', hint: 'payments switch — 0 = off' },
  payoutsEnabled: { label: 'Chef payouts', hint: 'off unless turned on here' },
  blueStarThresholdMeals: { label: 'Blue Star threshold (meals)' },
  blueStarThresholdDollarsCents: { label: 'Blue Star threshold ($)' },
  blueStarMaxPortions: { label: 'Blue Star max portions' },
  blueStarMaxPriceCents: { label: 'Blue Star max price' },
  chefTargetPerThousandResidents: { label: 'Chef target / 1k residents' },
  eaterTargetPerThousandResidents: { label: 'Eater target / 1k residents' },
  dishesListedTargetPerWeek: { label: 'Dishes listed / week target' },
  dishesSoldTargetPerWeek: { label: 'Dishes sold / week target' },
  activityLevelHighPct: { label: 'High activity at', hint: '% of sold target' },
  activityLevelMediumPct: { label: 'Medium activity at', hint: '% of sold target' },
  chefShortageEaterPerChefRatio: { label: 'Chef shortage above', hint: 'eaters per chef' },
  eaterShortageEaterPerChefRatio: { label: 'Eater shortage below', hint: 'eaters per chef' },
  maxShareConnectionLevel: { label: 'Max share connection level' },
};

export const GROUP_LABEL: Record<string, string> = {
  money: 'Money & tax',
  blue_star: 'Blue Star',
  norms: 'Supply & demand norms',
};

export function leverLabel(key: string): string {
  return LEVER_LABEL[key]?.label ?? key;
}

/** A lever value as shown: decimals as %, cents as $, bools as On/Off. */
export function formatLeverValue(lever: Pick<EffectiveLever, 'kind' | 'key'>, value: LeverValue | unknown): string {
  if (value === null || value === undefined) return '—';
  switch (lever.kind) {
    case 'decimal':
      return formatRate(Number(value), 'fraction', 2);
    case 'cents':
      return formatCents(Number(value));
    case 'bool':
      return value ? 'On' : 'Off';
    case 'string':
    case 'enum':
      return String(value) || '—';
    default:
      return String(value);
  }
}

/** Coverage and status tones used across the money screens. */
export const STATUS_TONE: Record<string, PillTone> = {
  active: 'success',
  pending: 'warning',
  closed: 'neutral',
  paid: 'success',
  in_transit: 'info',
  failed: 'danger',
  canceled: 'neutral',
  reconciled: 'success',
  needs_review: 'danger',
};

export function humanStatus(s: string): string {
  const t = s.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}
